import { Fragment, ReactNode, useContext, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import { Box, BoxProps, Button, ButtonProps, List, ListItem, Text, useToast } from '@bitrise/bitkit';
import groupBy from 'lodash/groupBy';
import orderBy from 'lodash/orderBy';

import { monolithApi } from '@/utils/api';
import { abortBuildPath, abortPipelinePath } from '@/utils/monolithApiRouteService';
import { GlobalPropsContext } from '@/components/page/GlobalPropsContext';
import BuildListItem, { BuildListErrorItem } from '@/components/BuildListItem/BuildListItem';
import BuildListSkeletonContent from '@/components/BuildListItem/BuildListSkeletonContent';
import BuildListLoadingErrorContent from '@/pages/dashboard/BuildsSection/BuildListLoadingErrorContent';
import BuildListEmptyContent from '@/pages/dashboard/BuildsSection/BuildListEmptyContent';
import { formatRelativeCustom } from '@/utils/commonFormats';
import { Build, PublicApiBuild } from '@/models/Build';
import BuildStatus from '@/models/BuildStatus';
import BuildListFilters from '@/pages/App/AppDetails/types/BuildListFilters';
import useStartBuild from '@/hooks/useStartBuild';
import ConfirmAbortBuildDialog from '@/components/ConfirmAbortBuildDialog/ConfirmAbortBuildDialog';
import AppForAppPage from '@/pages/App/types/AppForAppPage';

const LoadingSkeletonCount = 5;
const NextPageSkeletonCount = 3;
export const isSameAppWorkflowBuild = (build?: Build, otherBuild?: Build): boolean | undefined =>
  build &&
  otherBuild &&
  build.repository?.slug === otherBuild.repository?.slug &&
  build.triggeredWorkflow === otherBuild.triggeredWorkflow;

const getPreviousBuild = (builds: Build[], build: Build): Build | undefined =>
  builds?.find((b) => isSameAppWorkflowBuild(b, build) && b.status === BuildStatus.Success);

type FullWidthButtonProps = Omit<ButtonProps, 'level' | 'fullWidth'>;
const FullWidthButton = (props: FullWidthButtonProps): JSX.Element => (
  <Button variant="tertiary" width="100%" marginY="12" {...props} />
);

const LoadMoreButton = ({
  isFetchingNextPage,
  isShowingErrorResult,
  fetchNextPage,
}: {
  isFetchingNextPage: boolean;
  isShowingErrorResult: boolean;
  fetchNextPage: () => void;
}): JSX.Element => {
  const isShowingFetchNextPageButton = !isFetchingNextPage && !isShowingErrorResult;

  return (
    <>
      {isFetchingNextPage && <BuildListSkeletonContent count={NextPageSkeletonCount} showAppInfo />}
      {isShowingFetchNextPageButton && (
        <FullWidthButton isDisabled={isFetchingNextPage} onClick={() => fetchNextPage()}>
          Load more
        </FullWidthButton>
      )}
    </>
  );
};

type BuildListProps = {
  app: AppForAppPage;
  builds: PublicApiBuild[] | undefined;
  count: number;
  hasNextPage?: boolean;
  fetchNextPage: () => void;
  canRunBuild: boolean;
  refetch: () => void;
  isFetchingNextPage: boolean;
  isFetchingNextPageError: boolean;
  isFetching: boolean;
  isError: boolean;
  isSuccess: boolean;
  filters: BuildListFilters;
  refetchInterval?: number | false;
} & BoxProps;

const BuildList = ({
  app,
  builds,
  count,
  hasNextPage,
  fetchNextPage,
  refetch,
  isFetchingNextPage,
  isFetchingNextPageError,
  isFetching,
  isError,
  isSuccess,
  refetchInterval,
  canRunBuild,
  ...rest
}: BuildListProps): JSX.Element => {
  const buildsGroupedByDate = useMemo(
    () =>
      groupBy<Build>(orderBy(builds, ['triggeredAt'], ['desc']), (b: Build) =>
        // NOTE This is a potential issue, watch out for any missing build reported on the Dashboard page
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        formatRelativeCustom(new Date(b.triggeredAt!), new Date()),
      ),
    [builds],
  );

  const { user } = useContext(GlobalPropsContext);
  const toast = useToast();

  const [isAbortConfirmVisible, setAbortConfirmVisible] = useState(false);
  const [pipelineableToAbort, setPipelineableToAbort] = useState<Build | null>(null);

  const rebuildTriggeredBy = `manual-${user?.username}`;
  const isFetchingOnEmptyResult = isFetching && count === 0;
  const isShowingErrorResult = !isFetching && isError;
  const isShowingEmptyResult = !isFetching && count === 0;
  const isShowingErrorEmptyResult = isShowingEmptyResult && isError;
  const isShowingSuccessEmptyResult = isShowingEmptyResult && isSuccess;
  const isShowingErrorItemsResult = isShowingErrorResult && count > 0;
  const isShowingFetchingNextPageError = isShowingErrorResult && isFetchingNextPageError;

  const retryAction = () => (isFetchingNextPageError ? fetchNextPage() : refetch());

  const { mutate: startBuild } = useStartBuild(
    {
      appSlug: app.slug,
      buildTriggerToken: app.buildTriggerToken,
    },
    {
      onSuccess: ({ data }) => {
        if (data.results?.length === 1 && data.results[0].buildUrl) {
          window.location.href = data.results[0].buildUrl;
        }
      },
      onError: (error) => {
        let errorTitle = 'Failed to start build';
        let errorDescription: ReactNode = 'Unknown error';
        if (error.response?.data.errorMsg) {
          errorDescription = error.response?.data.errorMsg;
        } else if (error.response?.data.results?.length) {
          const resultErrors: string[] = [];
          error.response?.data.results.forEach((result) => {
            if (result.status === 'error') {
              resultErrors.push(`${result.triggeredPipeline || result.triggeredWorkflow}: ${result.message}`);
            }
          });
          if (resultErrors.length > 1) {
            errorTitle += 's';
          }
          errorDescription = (
            <>
              {resultErrors.map((err) => (
                <List>
                  <ListItem>{err}</ListItem>
                </List>
              ))}
            </>
          );
        } else {
          errorDescription = error.response?.data.message || 'Unknown error';
        }
        toast({
          title: errorTitle,
          description: errorDescription,
          status: 'error',
          isClosable: true,
        });
      },
    },
  );

  const { mutate: abortBuild } = useMutation({
    mutationFn: ({ buildSlug, data }: any) => {
      return monolithApi.request({
        url: abortBuildPath(buildSlug),
        method: 'POST',
        data,
      });
    },
    onSuccess: () => {
      setAbortConfirmVisible(false);
      setTimeout(() => {
        refetch();
      }, 1500);
    },
  });

  const { mutate: abortPipeline } = useMutation({
    mutationFn: ({ pipelineSlug, data }: any) => {
      return monolithApi.request({
        url: abortPipelinePath(app.slug, pipelineSlug),
        method: 'POST',
        data,
      });
    },
    onSuccess: () => {
      setAbortConfirmVisible(false);
      setTimeout(() => {
        refetch();
      }, 1500);
    },
  });

  const getBuildItem = (allBuilds: Build[], build: Build) => {
    return {
      build,
      previousBuild: getPreviousBuild(allBuilds, build),
      canRunBuild,
      showAppInfo: false,
      onRebuildClick: (selectedBuild: Build) => {
        const buildParams: any = build.originalBuildParams || build.triggerParams;

        if (build.isPipeline) {
          buildParams.pipelineId = selectedBuild.triggeredWorkflow;
        } else {
          buildParams.workflowId = selectedBuild.triggeredWorkflow;
        }

        delete buildParams.remoteAccess;

        startBuild({ buildParams, triggeredBy: rebuildTriggeredBy });
      },
      onAbortClick,
    };
  };

  const onAbortClick = (build: Build) => {
    setAbortConfirmVisible(true);
    setPipelineableToAbort(build);
  };

  const onAbortClose = () => {
    setAbortConfirmVisible(false);
    setPipelineableToAbort(null);
  };

  const onAbortConfirm = (abortReason: string) => {
    setAbortConfirmVisible(false);

    if (pipelineableToAbort?.isPipeline) {
      abortPipeline({
        pipelineSlug: pipelineableToAbort?.slug,
        data: {
          abort_reason: abortReason,
          abort_with_success: false,
          skip_notifications: true,
        },
      });
    } else {
      abortBuild({
        buildSlug: pipelineableToAbort?.slug,
        data: {
          abort_reason: abortReason,
          abort_with_success: false,
          skip_notifications: true,
        },
      });
    }
    setPipelineableToAbort(null);
  };

  return (
    <Box data-testid="build-list" paddingY={['16', '0']} {...rest}>
      <ConfirmAbortBuildDialog
        onConfirm={onAbortConfirm}
        onClose={onAbortClose}
        type="build"
        isOpen={isAbortConfirmVisible}
      />
      {isFetchingOnEmptyResult && <BuildListSkeletonContent count={LoadingSkeletonCount} showAppInfo />}
      {isShowingErrorEmptyResult && (
        <BuildListLoadingErrorContent message="Failed to load your builds!" retry={() => retryAction()} />
      )}
      {builds && (
        <div>
          {isShowingErrorItemsResult && !isFetchingNextPageError && (
            <BuildListErrorItem
              message="Failed to fetch your builds! Wait for auto-refresh or retry manually."
              retry={retryAction}
              marginY="24"
            />
          )}
          {Object.keys(buildsGroupedByDate).map((key) => (
            <Fragment key={key}>
              {buildsGroupedByDate[key].map((build, idx) => (
                <Fragment key={build.slug}>
                  {idx === 0 && (
                    <Text size="1" textColor="neutral.50" fontWeight="bold" textTransform="uppercase" marginBottom="8">
                      {key}
                    </Text>
                  )}
                  <BuildListItem key={build.slug} buildItem={getBuildItem(builds, build)} app={app} marginBottom="24" />
                </Fragment>
              ))}
            </Fragment>
          ))}
          {isShowingSuccessEmptyResult && <BuildListEmptyContent />}
          {isShowingFetchingNextPageError && (
            <BuildListErrorItem message="Failed to fetch more builds!" retry={() => retryAction()} />
          )}
        </div>
      )}
      {hasNextPage && (
        <LoadMoreButton
          isFetchingNextPage={isFetchingNextPage}
          isShowingErrorResult={isShowingErrorResult}
          fetchNextPage={fetchNextPage}
        />
      )}
    </Box>
  );
};

export { BuildListProps };
export default BuildList;
